import { useState } from 'react';
import { FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { StagedFile } from './useStagedFiles';

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentPreviewListProps {
  items: StagedFile[];
  onRemove: (id: string) => void;
  disabled?: boolean;
  className?: string;
}

// Thumbnail grid for staged intake attachments. Image files (pasted OR uploaded) show a
// real preview tile with a click-to-enlarge dialog; clipboard-originated ones carry a
// "Pasted" badge. Non-images fall back to an icon tile. Shared by both intake surfaces
// (86d3hdy52).
export function AttachmentPreviewList({
  items,
  onRemove,
  disabled,
  className,
}: AttachmentPreviewListProps) {
  const [preview, setPreview] = useState<StagedFile | null>(null);

  if (items.length === 0) return null;

  const totalSize = items.reduce((sum, s) => sum + s.file.size, 0);

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn('space-y-2', className)}>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(4.5rem,1fr))] gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="group relative aspect-square overflow-hidden rounded-md border bg-muted/30"
            >
              {item.isImage && item.previewUrl ? (
                <button
                  type="button"
                  onClick={() => setPreview(item)}
                  className="block h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Preview ${item.file.name}`}
                >
                  <img
                    src={item.previewUrl}
                    alt={item.file.name}
                    className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                </button>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                  <span
                    className="w-full truncate text-[10px] text-muted-foreground"
                    title={item.file.name}
                  >
                    {item.file.name}
                  </span>
                </div>
              )}

              {item.source === 'paste' && (
                <Badge
                  variant="secondary"
                  className="pointer-events-none absolute left-1 top-1 px-1 py-0 text-[9px] leading-tight"
                >
                  Pasted
                </Badge>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    disabled={disabled}
                    onClick={() => onRemove(item.id)}
                    aria-label={`Remove ${item.file.name}`}
                    title={`Remove ${item.file.name}`}
                    className="absolute right-1 top-1 h-5 w-5 rounded-full opacity-0 shadow transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove {item.file.name}</TooltipContent>
              </Tooltip>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          {items.length} file{items.length === 1 ? '' : 's'} · {formatFileSize(totalSize)}
        </p>
      </div>

      <Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogTitle className="sr-only">
            {preview ? `Preview of ${preview.file.name}` : 'Attachment preview'}
          </DialogTitle>
          {preview && (
            <div className="space-y-2">
              <img
                src={preview.previewUrl}
                alt={preview.file.name}
                className="mx-auto max-h-[70vh] w-auto rounded-md"
              />
              <p className="text-center text-xs text-muted-foreground">
                {preview.file.name} · {formatFileSize(preview.file.size)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
